import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Scale as ScaleIcon, RefreshCw, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => Number(n || 0).toLocaleString('zh-TW', { maximumFractionDigits: 2 });

const Row = ({ label, value, tone = '', testId }) => (
  <div className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
    <span className="text-xs text-neutral-500">{label}</span>
    <span className={`font-mono tabular-nums text-sm font-medium ${tone || 'text-neutral-800'}`} data-testid={testId}>
      {value}
    </span>
  </div>
);

export const CreditAuditCard = ({ userId, onRepaired }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${userId}/credit-audit`);
      setAudit(res.data);
    } catch {
      toast.error('載入點數稽核失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const repair = async () => {
    const amount = customAmount ? parseFloat(customAmount) : audit.discrepancy;
    if (!amount || amount <= 0) { toast.error('補回數量必須大於 0'); return; }
    if (!window.confirm(`確定為此用戶補回 ${fmt(amount)} 點（無限期）？`)) return;
    setRepairing(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${userId}/credit-audit/repair`, {
        amount: customAmount ? amount : null,
        note,
      });
      toast.success(`已補回 ${fmt(res.data.restored)} 點`);
      setAudit(res.data.audit);
      setCustomAmount('');
      setNote('');
      onRepaired?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || '補回失敗');
    } finally {
      setRepairing(false);
    }
  };

  if (loading || !audit) {
    return <div className="bg-white rounded-2xl shadow-xl p-6 border border-neutral-200 text-sm text-neutral-500" data-testid="credit-audit-loading">稽核載入中...</div>;
  }

  const hasGap = Math.abs(audit.discrepancy) >= 0.01;
  const gapTone = audit.discrepancy > 0 ? 'text-red-600' : audit.discrepancy < 0 ? 'text-amber-600' : 'text-green-600';

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 border border-neutral-200 mb-6" data-testid="credit-audit-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ScaleIcon className="w-5 h-5 text-primary-600" strokeWidth={2} />
          <h3 className="text-lg font-semibold text-neutral-800">點數稽核</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-xl" data-testid="credit-audit-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-x-8">
        <div>
          <Row label={`註冊贈送`} value={`+${fmt(audit.initial_credits)}`} testId="credit-audit-initial" />
          <Row label="Stripe 充值合計" value={`+${fmt(audit.total_purchased)}`} tone="text-green-700" testId="credit-audit-purchased" />
          <Row label="管理員調整合計" value={`${audit.total_admin_adjustment >= 0 ? '+' : ''}${fmt(audit.total_admin_adjustment)}`} testId="credit-audit-admin" />
          <Row label="使用扣除合計" value={`-${fmt(audit.total_spent)}`} tone="text-neutral-700" testId="credit-audit-spent" />
          <Row label="到期扣除合計" value={`-${fmt(audit.total_expired)}`} tone="text-red-600" testId="credit-audit-expired" />
          {audit.total_reconciled > 0 && (
            <Row label="稽核對帳（歷史未記錄）" value={`-${fmt(audit.total_reconciled)}`} tone="text-orange-600" testId="credit-audit-reconciled" />
          )}
        </div>
        <div>
          <Row label="依記錄應有餘額" value={fmt(audit.expected_balance)} testId="credit-audit-expected" />
          <Row label="目前實際餘額" value={fmt(audit.current_balance)} testId="credit-audit-current" />
          <Row label="無法解釋的差額" value={`${audit.discrepancy > 0 ? '-' : audit.discrepancy < 0 ? '+' : ''}${fmt(Math.abs(audit.discrepancy))}`} tone={gapTone} testId="credit-audit-discrepancy" />
          <Row label="有效套餐剩餘合計" value={fmt(audit.packaged_remaining)} testId="credit-audit-packaged" />
          <Row label="記錄筆數" value={audit.log_count} testId="credit-audit-log-count" />
        </div>
      </div>

      {audit.last_credit_expiry && (
        <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="credit-audit-last-expiry">
          最近一次到期扣除：{fmt(audit.last_credit_expiry.amount)} 點 · {new Date(audit.last_credit_expiry.at).toLocaleString('zh-TW')}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-neutral-200">
        <p className="text-xs text-neutral-500 mb-3">
          {hasGap
            ? audit.discrepancy > 0
              ? `實際餘額比記錄少 ${fmt(audit.discrepancy)} 點（舊版到期機制未留記錄所致），可一鍵補回為無限期點數。`
              : `實際餘額比記錄多 ${fmt(Math.abs(audit.discrepancy))} 點，無需補回。`
            : '餘額與記錄一致，無需補回。'}
        </p>
        <div className="flex flex-col md:flex-row gap-2">
          <Input
            type="number"
            step="0.01"
            placeholder={audit.discrepancy > 0 ? `預設補回 ${fmt(audit.discrepancy)}` : '自訂補回數量'}
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            className="rounded-xl md:w-44"
            data-testid="credit-audit-amount-input"
          />
          <Input
            placeholder="備註（可選）"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-xl flex-1"
            data-testid="credit-audit-note-input"
          />
          <Button
            onClick={repair}
            disabled={repairing || (!customAmount && audit.discrepancy <= 0)}
            className="rounded-xl bg-primary-600 text-white"
            data-testid="credit-audit-repair-button"
          >
            <Wrench className="w-4 h-4 mr-2" />
            {repairing ? '處理中...' : '補回點數'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreditAuditCard;
